<template>
  <div class="careers-page">
    <!-- Carousel Hero Section -->
    <ServicesItsCarouselComp 
      :slides="heroSlides" 
      :backgroundImage="backgroundImage"
      :patternImage="patternImage"
      :missionVision="missionVision"
    />

    <!-- Intro Section -->
    <CareersItsIntroComp :content="introContent" />

    <!-- Benefits & Culture Section -->
    <CareersItsBenefitsComp :content="benefitsContent" />

    <!-- Open Positions Section -->
    <CareersItsPositionsComp :content="positionsContent" />

    <!-- Call to Action Section -->
    <CareersItsCtaComp :content="ctaContent" />
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import WOW from 'wowjs'
import ServicesItsCarouselComp from '@/components/ServicesItsCarouselComp.vue'
import CareersItsIntroComp from '@/components/CareersItsIntroComp.vue'
import CareersItsBenefitsComp from '@/components/CareersItsBenefitsComp.vue'
import CareersItsPositionsComp from '@/components/CareersItsPositionsComp.vue'
import CareersItsCtaComp from '@/components/CareersItsCtaComp.vue'

export default {
  name: 'CareersPage',
  components: {
    ServicesItsCarouselComp,
    CareersItsIntroComp,
    CareersItsBenefitsComp,
    CareersItsPositionsComp,
    CareersItsCtaComp
  },
  setup() {
    // Hero carousel data
    const backgroundImage = ref('/resource/page-title-bg.jpg')
    const patternImage = ref('/shapes/shape-06.png')
    
    const heroSlides = ref([
      {
        subtitle: "Join the Team",
        titleLines: [
          { text: "Join the Team That's " },
          { text: "Building ", highlight: "Tomorrow's Tech" }
        ],
        description: "Be part of a dynamic team that's shaping the future of technology. Join us in creating innovative solutions and advancing your career.",
        buttonText: "View Openings",
        buttonLink: "/contact"
      },
      {
        subtitle: "Open Roles",
        titleLines: [
          { text: "Engineering, Marketing, ", highlight: "Research, QA, Design" }
        ],
        description: "Explore exciting opportunities across multiple disciplines. We're looking for passionate individuals to join our growing team.",
        buttonText: "Learn More",
        buttonLink: "/contact"
      },
      {
        subtitle: "Perks",
        titleLines: [
          { text: "Hybrid Work, Tech Setup, ", highlight: "Insurance, Career Growth" }
        ],
        description: "Enjoy competitive compensation, comprehensive health coverage, flexible work arrangements, and continuous learning opportunities.",
        buttonText: "Apply Now",
        buttonLink: "#apply-now-section"
      }
    ])

    const missionVision = ref({
      vision: {
        title: "Our Vision",
        description: "To be the most trusted software innovation partner, building meaningful technology for real-world challenges."
      },
      mission: {
        title: "Our Mission",
        description: "To build meaningful technology for real-world challenges and create an inclusive workplace where our team members can thrive and grow."
      }
    })

    // Intro content
    const introContent = ref({
      header: {
        label: "Join Our Team",
        title: "Why Work with Sogdo",
        description: "Be part of a dynamic team that's shaping the future of technology. We offer exciting opportunities, competitive benefits, and a culture that values innovation, collaboration, and growth."
      }
    })

    // Benefits content
    const benefitsContent = ref({
      header: {
        label: "Our Culture",
        title: "Perks & Benefits",
        description: "We believe in taking care of our team members and creating an environment where everyone can thrive and grow both personally and professionally."
      },
      benefits: [
        {
          icon: "icon-01",
          title: "Hybrid Work",
          description: "Flexible work arrangements that allow you to work from home or our office, giving you the best of both worlds.",
          features: ["Remote work options", "Flexible hours", "Work-life balance"]
        },
        {
          icon: "icon-02",
          title: "Tech Setup",
          description: "Latest technology equipment and tools to help you work efficiently and stay productive in your role.",
          features: ["Latest hardware", "Software licenses", "Development tools"]
        },
        {
          icon: "icon-03",
          title: "Insurance",
          description: "Comprehensive health coverage, mental health support, and wellness programs to keep our team healthy and happy.",
          features: ["Health insurance", "Dental & vision", "Life insurance"]
        },
        {
          icon: "icon-04",
          title: "Career Growth",
          description: "Continuous learning opportunities, mentorship programs, and clear career progression paths to help you advance.",
          features: ["Learning budget", "Mentorship programs", "Career progression"]
        },
        {
          icon: "icon-05",
          title: "Internships & Graduate Hiring",
          description: "We welcome fresh talent through our internship programs and graduate hiring initiatives to build the next generation of tech leaders.",
          features: ["Summer internships", "Graduate programs", "Mentorship support"]
        },
        {
          icon: "icon-01",
          title: "Life at Sogdo",
          description: "Collaborative environment with regular team events, knowledge sharing sessions, and a supportive community of talented professionals.",
          features: ["Team events", "Knowledge sharing", "Supportive community"]
        }
      ]
    })

    // Positions content
    const positionsContent = ref({
      header: {
        label: "Current Openings",
        title: "Open Positions",
        description: "Explore our current job openings and find the perfect role to advance your career with us."
      },
      positions: [
        {
          title: "Senior Full-Stack Developer",
          location: "San Francisco, CA / Remote",
          description: "We're looking for an experienced full-stack developer to join our engineering team. You'll work on cutting-edge web applications using modern technologies like React, Node.js, and cloud platforms.",
          requirements: [
            "5+ years of full-stack development experience",
            "Proficiency in React, Node.js, and TypeScript",
            "Experience with cloud platforms (AWS, Azure, or GCP)",
            "Strong problem-solving and communication skills"
          ],
          applyLink: "#apply-now-section"
        },
        {
          title: "AI/ML Engineer",
          location: "San Francisco, CA / Remote",
          description: "Join our AI team to develop and implement machine learning solutions. You'll work on projects involving computer vision, natural language processing, and predictive analytics.",
          requirements: [
            "3+ years of ML/AI development experience",
            "Proficiency in Python, TensorFlow, and PyTorch",
            "Experience with data science and analytics",
            "Strong mathematical and statistical background"
          ],
          applyLink: "#apply-now-section"
        },
        {
          title: "UX/UI Designer",
          location: "San Francisco, CA / Remote",
          description: "We're seeking a creative UX/UI designer to create intuitive and engaging user experiences. You'll work closely with our product and engineering teams to design user-centered solutions.",
          requirements: [
            "4+ years of UX/UI design experience",
            "Proficiency in Figma, Sketch, and Adobe Creative Suite",
            "Experience with user research and testing",
            "Strong portfolio showcasing design skills"
          ],
          applyLink: "#apply-now-section"
        },
        {
          title: "DevOps Engineer",
          location: "San Francisco, CA / Remote",
          description: "Join our DevOps team to build and maintain our cloud infrastructure. You'll work on automation, monitoring, and deployment pipelines to ensure our systems are reliable and scalable.",
          requirements: [
            "3+ years of DevOps/Infrastructure experience",
            "Experience with Kubernetes, Docker, and CI/CD",
            "Knowledge of cloud platforms (AWS, Azure, or GCP)",
            "Strong scripting and automation skills"
          ],
          applyLink: "#apply-now-section"
        },
        {
          title: "Product Manager",
          location: "San Francisco, CA / Remote",
          description: "Lead product strategy and development for our technology solutions. You'll work with cross-functional teams to define product requirements and drive successful product launches.",
          requirements: [
            "4+ years of product management experience",
            "Experience with agile development methodologies",
            "Strong analytical and communication skills",
            "Technical background preferred"
          ],
          applyLink: "#apply-now-section"
        },
        {
          title: "Blockchain Developer",
          location: "San Francisco, CA / Remote",
          description: "Develop blockchain solutions and smart contracts. You'll work on decentralized applications, DeFi protocols, and innovative blockchain technologies.",
          requirements: [
            "3+ years of blockchain development experience",
            "Proficiency in Solidity, Web3, and smart contracts",
            "Experience with Ethereum and other blockchain platforms",
            "Understanding of DeFi and NFT technologies"
          ],
          applyLink: "#apply-now-section"
        }
      ]
    })

    // CTA content
    const ctaContent = ref({
      header: {
        title: "Don't See Your Role?",
        description: "We're always looking for talented individuals to join our team. If you don't see a position that matches your skills, we'd still love to hear from you."
      },
      actions: {
        primary: {
          text: "Get In Touch",
          link: "/contact"
        },
        secondary: {
          text: "Send Your Resume",
          link: "mailto:careers@sogdo.com"
        }
      }
    })

    onMounted(() => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false
      }).init()
    })

    return {
      heroSlides,
      backgroundImage,
      patternImage,
      missionVision,
      introContent,
      benefitsContent,
      positionsContent,
      ctaContent
    }
  }
}
</script>

<style scoped>
/* Careers page specific styles */
</style>